/**
 * Admin Audit Log Page
 *
 * Filterable list of admin audit log entries, grouped by date. Filters
 * (date, action type, admin email) are reflected in the URL query string so
 * a filtered view can be copied and shared. The action-type options come
 * from the event types seen on the current page rather than a hard-coded
 * list, since the audit log's event types are open-ended.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar, Clock, Shield, User } from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminAuditLogs } from "@/hooks/useAdmin";
import type { AuditLogEntry } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const EVENT_COLORS: Record<string, string> = {
  user_update: "bg-blue-100 text-blue-700",
  user_disable: "bg-red-100 text-red-700",
  user_enable: "bg-green-100 text-green-700",
  password_reset: "bg-amber-100 text-amber-700",
};

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** The label shown for an entry's action — see EVENT_COLORS' own keys. */
function actionOf(entry: AuditLogEntry): string {
  return entry.action ?? entry.eventType;
}

interface AuditFilters {
  date: string;
  action: string;
  actorEmail: string;
}

function filtersFromParams(params: URLSearchParams): AuditFilters {
  return {
    date: params.get("date") ?? todayISO(),
    action: params.get("action") ?? "",
    actorEmail: params.get("actorEmail") ?? "",
  };
}

function paramsFromFilters(filters: AuditFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.date && filters.date !== todayISO())
    params.set("date", filters.date);
  if (filters.action) params.set("action", filters.action);
  if (filters.actorEmail) params.set("actorEmail", filters.actorEmail);
  return params;
}

export function AdminAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(() => filtersFromParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filters, setFilters] = useState<AuditFilters>(initialFilters);
  const [emailInput, setEmailInput] = useState(initialFilters.actorEmail);
  const [cursor, setCursor] = useState<string | undefined>();
  const [previousEntries, setPreviousEntries] = useState<AuditLogEntry[]>([]);

  const emailTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const applyFilters = useCallback(
    (next: AuditFilters) => {
      setFilters(next);
      setCursor(undefined);
      setPreviousEntries([]);
      setSearchParams(paramsFromFilters(next), { replace: true });
    },
    [setSearchParams]
  );

  const handleDateChange = useCallback(
    (newDate: string) => {
      applyFilters({ ...filters, date: newDate });
    },
    [applyFilters, filters]
  );

  const handleActionChange = useCallback(
    (action: string) => {
      applyFilters({ ...filters, action });
    },
    [applyFilters, filters]
  );

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmailInput(value);
      if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current);
      emailTimeoutRef.current = setTimeout(() => {
        applyFilters({ ...filters, actorEmail: value.trim() });
      }, 400);
    },
    [applyFilters, filters]
  );

  useEffect(() => {
    return () => {
      if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current);
    };
  }, []);

  function handleClearFilters() {
    setEmailInput("");
    applyFilters({ date: todayISO(), action: "", actorEmail: "" });
  }

  const hasActiveFilters = filters.action !== "" || filters.actorEmail !== "";

  const { data, isLoading, error } = useAdminAuditLogs({
    date: filters.date,
    action: filters.action || undefined,
    actorEmail: filters.actorEmail || undefined,
    cursor,
    limit: 50,
  });

  const displayEntries = useMemo(
    () => [...previousEntries, ...(data?.entries ?? [])],
    [previousEntries, data?.entries]
  );

  // Every distinct action/event type seen so far, for the action filter —
  // pulled from the data on hand rather than hard-coded, since the audit
  // log's event types are open-ended and can grow without this page's code
  // changing. Includes the currently-selected action even if the current
  // page doesn't have it, so the dropdown never silently drops the filter
  // that's actually applied.
  const actionOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const entry of displayEntries) seen.add(actionOf(entry));
    if (filters.action) seen.add(filters.action);
    return [...seen].sort();
  }, [displayEntries, filters.action]);

  function handleLoadMore() {
    if (data?.entries) {
      setPreviousEntries((prev) => [...prev, ...data.entries]);
    }
    setCursor(data?.cursor);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          Audit Logs
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Admin action history. All write operations are recorded.
        </p>
      </div>

      {/* Filters: date, action type, admin email */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label
            htmlFor="audit-date"
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          >
            <Calendar className="h-3.5 w-3.5" />
            Date
          </label>
          <input
            id="audit-date"
            type="date"
            value={filters.date}
            max={todayISO()}
            onChange={(e) => handleDateChange(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="audit-action"
            className="text-xs font-medium text-muted-foreground"
          >
            Action
          </label>
          <select
            id="audit-action"
            value={filters.action}
            onChange={(e) => handleActionChange(e.target.value)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All actions</option>
            {actionOptions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="audit-actor-email"
            className="text-xs font-medium text-muted-foreground"
          >
            Admin email
          </label>
          <input
            id="audit-actor-email"
            type="email"
            value={emailInput}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="admin@example.com"
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Log entries */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          {getErrorMessage(error, "Failed to load audit logs.")}
        </div>
      ) : (
        <>
          {displayEntries.length === 0 && !isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {hasActiveFilters
                ? `No audit events match your filters on ${filters.date}.`
                : `No audit events on ${filters.date}.`}
            </div>
          ) : (
            <div className="space-y-2">
              {displayEntries.map((entry, i) => (
                <div
                  key={`${entry.timestamp}-${i}`}
                  className="rounded-xl border border-border/50 bg-card p-4"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={cn(
                              "rounded-full px-2.5 py-0.5 text-xs font-medium",
                              EVENT_COLORS[entry.eventType] ??
                                "bg-zinc-100 text-zinc-700"
                            )}
                          >
                            {actionOf(entry)}
                          </span>
                          {entry.targetType && (
                            <span className="text-xs text-muted-foreground">
                              on {entry.targetType}
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {entry.adminUserId && (
                            <span
                              className="inline-flex items-center gap-1"
                              title={entry.adminUserId}
                            >
                              <User className="h-3 w-3" />
                              Admin:{" "}
                              {entry.adminName ||
                                entry.adminEmail ||
                                `${entry.adminUserId.slice(0, 8)}…`}
                            </span>
                          )}
                          {entry.userId && (
                            <span
                              className="inline-flex items-center gap-1"
                              title={entry.userId}
                            >
                              <User className="h-3 w-3" />
                              Target:{" "}
                              {entry.userName ||
                                entry.userEmail ||
                                `${entry.userId.slice(0, 8)}…`}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(entry.timestamp)}
                          </span>
                        </div>
                        {Object.keys(entry.details ?? {}).length > 0 && (
                          <pre className="mt-2 max-w-lg overflow-x-auto rounded bg-muted/50 p-2 text-xs">
                            {JSON.stringify(entry.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>

                    {entry.adminRole && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {entry.adminRole}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {data?.cursor && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
